import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { ArrowLeft, Pencil, Trash2, Plus, LogOut, Wand2, ArrowRight, Mail } from 'lucide-react';
import ImageLightbox from '../components/ImageLightbox';
import { adminAuth } from '../services/adminAuth';

interface Property {
  id: string;
  address: string;
  status: string;
  zillow_url?: string | null;
  original_image?: string | null;
  pencil_image_url: string | null;
  oil_image_url: string | null;
  charcoal_image_url: string | null;
  chalk_image_url: string | null;
  price?: string;
  created_at: string;
  user_id?: string;
}

interface EditingProperty extends Partial<Property> {
  id: string;
}

const WEBHOOK_URL = 'https://hook.us2.make.com/vbqmo12mnwgi8b53eu5cwegdw2f6qb46';

const Admin: React.FC = () => {
  const navigate = useNavigate();
  const [properties, setProperties] = useState<Property[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingProperty, setEditingProperty] = useState<EditingProperty | null>(null);
  const [showMagicModal, setShowMagicModal] = useState(false);
  const [location, setLocation] = useState('');
  const [magicLoading, setMagicLoading] = useState(false);
  const [generationStatus, setGenerationStatus] = useState<GenerationStatus>('idle');
  const [lightbox, setLightbox] = useState<{
    images: { url: string; label: string }[];
    currentIndex: number;
    property?: Property;
  } | null>(null);
  const [currentMode, setCurrentMode] = useState<'test' | 'live'>('live'); // Default to live mode

  const fetchProperties = async () => {
    try {
      const { data, error } = await supabase
        .from('properties')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setProperties(data || []);
      return data || [];
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      return [];
    } finally {
      setLoading(false);
    }
  };

  const openLightbox = (property: Property, initialImage: 'original' | 'ai') => {
    const images = [];
    
    // Always include original image if it exists
    if (property.original_image) {
      images.push({ url: property.original_image, label: 'Original' });
    }

    // Add all available AI-generated images with valid URLs
    if (property.pencil_image_url?.trim()) {
      images.push({ url: property.pencil_image_url, label: 'Pencil Style' });
    }
    if (property.oil_image_url?.trim()) {
      images.push({ url: property.oil_image_url, label: 'Oil Style' });
    }
    if (property.charcoal_image_url?.trim()) {
      images.push({ url: property.charcoal_image_url, label: 'Charcoal Style' });
    }
    if (property.chalk_image_url?.trim()) {
      images.push({ url: property.chalk_image_url, label: 'Chalk Style' });
    }
    
    if (images.length > 0) {
      const startIndex = initialImage === 'original' ? 0 : 1;

      setLightbox({
        images,
        currentIndex: startIndex,
        property
      });
    }
  };

  const handleMagicCreate = async () => {
    if (!lightbox?.property) return;
    
    setError(null);
    setGenerationStatus('generating');

    if (!lightbox.property.original_image) {
      setError('No original image available for this property');
      setGenerationStatus('error');
      return;
    }
    
    try {
      const response = await fetch('/.netlify/functions/replicate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          version: "batouresearch/sdxl-controlnet-lora:3bb13fe1c33c35987b33792b01b71ed6529d03f165d1c2416375859f09ca9fef",
          input: {
            image: lightbox.property.original_image,
            prompt: "watercolor painting of a house, artistic, professional",
            lora_weights: "https://pbxt.replicate.delivery/mwN3AFyYZyouOB03Uhw8ubKW9rpMgdtL9zYV9GF2WGDiwbE/trained_model.tar",
            refine_steps: 20,
            condition_scale: 0.5,
            num_inference_steps: 40
          }
        })
      });

      if (!response.ok) {
        throw new Error(`Webhook error: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      
      if (!result.output) {
        throw new Error('No response from webhook');
      }

      // Update the property with the new chalk image
      const { error: updateError } = await supabase
        .from('properties')
        .update({
          chalk_image_url: result.output[0],
          status: 'completed'
        })
        .eq('id', lightbox.property.id);

      if (updateError) throw updateError;

      setGenerationStatus('completed');
      await fetchProperties();

      // Update lightbox with new images if it's still open
      const { data: updatedProperty } = await supabase
        .from('properties')
        .select('*')
        .eq('id', lightbox.property.id)
        .single();

      if (updatedProperty && lightbox?.property?.id === updatedProperty.id) {
        openLightbox(updatedProperty, 'ai');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to generate image';
      console.error('Error generating image:', errorMessage);
      setGenerationStatus('error');
      setError(errorMessage);
    }
  };

  const handleWebhook = async () => {
    if (!lightbox?.property) return;
    
    setError(null);
    let responseText = '';
    
    try {
      // First update the status to pending
      const { error: statusError } = await supabase
        .from('properties')
        .update({ status: 'pending' })
        .eq('id', lightbox.property.id);

      if (statusError) throw statusError;

      // Then trigger the webhook
      const response = await fetch('https://hook.us2.make.com/w9x96v7yf5hnosl8rrpaf33f7imqa28h', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: lightbox.property.id,
          image_url: lightbox.property.original_image || null
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to trigger webhook: ${response.status} ${response.statusText}`);
      }

      // Parse the JSON response
      try {
        responseText = await response.text();
        const data = JSON.parse(responseText);
      
        if (!data || !data.image_url) {
          throw new Error('Invalid response from webhook');
        }

        // Update the property with the new image URL
        const { error: updateError } = await supabase
          .from('properties')
          .update({
            pencil_image_url: data.image_url,
            status: 'completed'
          })
          .eq('id', lightbox.property.id);

        if (updateError) throw updateError;

        setError('Image generated and saved successfully!');
      } catch (parseError) {
        console.error('Response parsing error:', parseError);
        throw new Error(`Invalid response format: ${responseText.slice(0, 100)}...`);
      }
      
      // Refresh properties after webhook
      await fetchProperties();
      
      // Update lightbox with new images if it's still open
      if (lightbox?.property?.id === lightbox.property.id) {
        const { data: updatedProperty } = await supabase
          .from('properties')
          .select('*')
          .eq('id', lightbox.property.id)
          .single();

        if (updatedProperty) {
          openLightbox(updatedProperty, 'ai');
        }
      }
    } catch (error) {
      console.error('Error triggering webhook:', error);
      setError(error instanceof Error ? error.message : 'Failed to trigger webhook');
      
      // Update status to failed if there was an error
      await supabase
        .from('properties')
        .update({ status: 'failed' })
        .eq('id', lightbox.property.id);
    }
  };

  const closeLightbox = () => setLightbox(null);

  const nextImage = () => {
    if (lightbox && lightbox.currentIndex < lightbox.images.length - 1) {
      setLightbox({
        ...lightbox,
        currentIndex: lightbox.currentIndex + 1
      });
    }
  };

  const previousImage = () => {
    if (lightbox && lightbox.currentIndex > 0) {
      setLightbox({
        ...lightbox,
        currentIndex: lightbox.currentIndex - 1
      });
    }
  };

  const handleMagicAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    setMagicLoading(true);
    setError(null);
    const area = location.trim();
    
    try {
      if (!area) {
        throw new Error('Please enter an area');
      }

      // Trigger the webhook with just the area
      const webhookResponse = await fetch(WEBHOOK_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          area
        })
      });

      if (!webhookResponse.ok) {
        throw new Error('Failed to trigger property processing');
      }
      
      setShowMagicModal(false);
      setLocation('');
      setError('Property search initiated successfully!');

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add property');
    } finally {
      setMagicLoading(false);
    }
  };

  // Poll for property updates
  useEffect(() => {
    const pollInterval = setInterval(async () => {
      const pendingProperties = properties.filter(p => p.status === 'pending');
      if (pendingProperties.length > 0) {
        await fetchProperties();
      }
    }, 5000); // Poll every 5 seconds

    return () => clearInterval(pollInterval);
  }, [properties]);

  // Check authentication on component mount
  useEffect(() => {
    if (!adminAuth.isAuthenticated()) {
      navigate('/admin-login');
      return;
    }
    fetchProperties();
  }, [navigate]);

  useEffect(() => {
    // Load current mode from sessionStorage on component mount
    const savedMode = sessionStorage.getItem('admin_mode_override');
    if (savedMode === 'test') {
      setCurrentMode('test');
    } else {
      setCurrentMode('live'); // Default to live mode
    }
  }, []);

  const handleSignOut = async () => {
    adminAuth.logout();
    navigate('/admin-login');
  };

  const handleModeChange = (mode: 'test' | 'live') => {
    setCurrentMode(mode);
    sessionStorage.setItem('admin_mode_override', mode);
    console.log(`Mode switched to: ${mode}`);
  };

  const handleGoToLanding = () => {
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  if (!adminAuth.isAuthenticated()) {
    return null;
  }

  const deleteProperty = async (id: string) => {
    try {
      const { error } = await supabase
        .from('properties')
        .delete()
        .eq('id', id);

      if (error) throw error;
      setProperties(properties.filter(prop => prop.id !== id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete property');
    }
  };

  const startEditing = (property: Property) => {
    setEditingProperty(property);
  };

  const cancelEditing = () => {
    setEditingProperty(null);
  };

  const saveEditing = async () => {
    if (!confirm('Are you sure you want to delete this property')) return;

    try {
      const { error } = await supabase
        .from('properties')
        .update({
          address: editingProperty.address,
          zillow_url: editingProperty.zillow_url,
          original_image: editingProperty.original_image,
          pencil_image_url: editingProperty.pencil_image_url,
          oil_image_url: editingProperty.oil_image_url,
          charcoal_image_url: editingProperty.charcoal_image_url,
          chalk_image_url: editingProperty.chalk_image_url,
          status: editingProperty.status || 'pending',
          price: editingProperty.price || '',
          score: editingProperty.score || null,
          color: editingProperty.color || null,
          prompt: editingProperty.prompt || null
        })
        .eq('id', editingProperty.id);

      if (error) throw error;
      // Refresh the properties list
      await fetchProperties();
      setEditingProperty(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update property');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Link
              to="/"
              className="text-gray-500 hover:text-gray-700 transition-colors"
            >
              <ArrowLeft className="w-6 h-6" />
            </Link>
            <button
              onClick={handleSignOut}
              className="ml-auto text-gray-500 hover:text-gray-700 transition-colors"
            >
              <LogOut className="w-6 h-6" />
            </button>
            <h1 className="text-2xl font-semibold text-gray-900">
              Property Management
            </h1>
          </div>
          <div className="flex gap-4">
            <Link
              to="/admin/email-campaign"
              className="inline-flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
            >
              <Mail className="w-4 h-4" />
              Email Campaign
            </Link>
            <button
              onClick={() => setShowMagicModal(true)}
              className="inline-flex items-center gap-2 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors"
            >
              <Wand2 className="w-4 h-4" />
              Magic Add
            </button>
            <Link
              to="/admin/new"
              className="inline-flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add Property
            </Link>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 text-red-600 p-4 rounded-lg mb-6">
            {error}
          </div>
        )}

        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Address
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Price
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Details
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Zillow URL
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Original Image
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  AI Images
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created At
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {properties.map((property) => (
                <tr key={property.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    {editingProperty?.id === property.id ? (
                      <input
                        type="text"
                        value={editingProperty.address || ''}
                        onChange={(e) => setEditingProperty(prev => ({
                          ...prev!,
                          address: e.target.value
                        }))}
                        className="w-full px-2 py-1 text-sm border rounded"
                      />
                    ) : (
                      <div className="text-sm text-gray-900">{property.address}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {editingProperty?.id === property.id ? (
                      <input
                        type="text"
                        value={editingProperty.price || ''}
                        onChange={(e) => setEditingProperty(prev => ({
                          ...prev!,
                          price: e.target.value
                        }))}
                        className="w-full px-2 py-1 text-sm border rounded"
                      />
                    ) : (
                      <div className="text-sm text-gray-900">{property.price || '-'}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">
                      {editingProperty?.id === property.id ? (
                        <div className="space-y-2">
                          <input
                            type="number"
                            placeholder="Score"
                            value={editingProperty.score ?? ''}
                            onChange={(e) => setEditingProperty(prev => ({
                              ...prev!,
                              score: parseInt(e.target.value)
                            }))}
                            className="w-full px-2 py-1 text-sm border rounded"
                          />
                          <input
                            type="text"
                            placeholder="Color"
                            value={editingProperty.color ?? ''}
                            onChange={(e) => setEditingProperty(prev => ({
                              ...prev!,
                              color: e.target.value
                            }))}
                            className="w-full px-2 py-1 text-sm border rounded"
                          />
                        </div>
                      ) : (
                        <div className="space-y-1">
                          {(property.score || property.score === 0) && (
                            <div className="flex items-center gap-2">
                              <span className="font-medium">Score:</span>
                              <span>{property.score}</span>
                            </div>
                          )}
                          {property.color && (
                            <div className="flex items-center gap-2">
                              <span className="font-medium">Color:</span>
                              <span>{property.color}</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {editingProperty?.id === property.id ? (
                      <input
                        type="text"
                        value={editingProperty.zillow_url || ''}
                        onChange={(e) => setEditingProperty(prev => ({
                          ...prev!,
                          zillow_url: e.target.value
                        }))}
                        className="w-full px-2 py-1 text-sm border rounded"
                      />
                    ) : (
                      property.zillow_url ? (
                        <a
                          href={property.zillow_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-blue-600 hover:text-blue-800 truncate max-w-xs block"
                        >
                          View on Zillow
                        </a>
                      ) : (
                        <span className="text-sm text-gray-400">No link</span>
                      )
                    )}
                  </td>
                  <td className="px-6 py-4">
                    {editingProperty?.id === property.id ? (
                      <input
                        type="text"
                        value={editingProperty.original_image || ''}
                        onChange={(e) => setEditingProperty(prev => ({
                          ...prev!,
                          original_image: e.target.value
                        }))}
                        className="w-full px-2 py-1 text-sm border rounded"
                      />
                    ) : (
                      property.original_image ? (
                        <img
                          onClick={() => openLightbox(property, 'original')}
                          src={property.original_image}
                          alt="Original"
                          className="w-16 h-16 object-cover rounded cursor-pointer hover:opacity-75 transition-opacity"
                        />
                      ) : (
                        <span className="text-sm text-gray-400">No image</span>
                      )
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-500">
                      {editingProperty?.id === property.id ? (
                        <div className="space-y-2">
                          {[
                            { label: 'Pencil', url: editingProperty.pencil_image_url, key: 'pencil_image_url' },
                            { label: 'Oil', url: editingProperty.oil_image_url, key: 'oil_image_url' },
                            { label: 'Charcoal', url: editingProperty.charcoal_image_url, key: 'charcoal_image_url' },
                            { label: 'Chalk', url: editingProperty.chalk_image_url, key: 'chalk_image_url' }
                          ].map(({ label, url, key }) => (
                            <div key={key} className="flex items-center gap-2">
                              <div className="w-8 h-8 rounded overflow-hidden bg-gray-100 flex-shrink-0">
                                {url && (
                                  <img src={url} alt={label} className="w-full h-full object-cover" />
                                )}
                              </div>
                              <input
                                type="text"
                                placeholder={`${label} Image URL`}
                                value={url || ''}
                                onChange={(e) => setEditingProperty(prev => ({
                                  ...prev!,
                                  [key]: e.target.value
                                }))}
                                className="flex-1 px-2 py-1 text-sm border rounded"
                              />
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="flex gap-2">
                          {(property.pencil_image_url || property.oil_image_url || 
                            property.charcoal_image_url || property.chalk_image_url) && (
                            <button
                              onClick={() => openLightbox(property, 'ai')}
                              className="flex gap-2 hover:opacity-75 transition-opacity"
                            >
                              {[
                                { url: property.pencil_image_url, label: 'Pencil' },
                                { url: property.oil_image_url, label: 'Oil' },
                                { url: property.charcoal_image_url, label: 'Charcoal' },
                                { url: property.chalk_image_url, label: 'Chalk' }
                              ].map(({ url, label }) => url && url.trim() !== '' && (
                                <div
                                  key={label}
                                  className="w-10 h-10 rounded overflow-hidden bg-gray-100 relative group"
                                  title={label}
                                >
                                  <img src={url} alt={label} className="w-full h-full object-cover" />
                                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all" />
                                </div>
                              ))}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {editingProperty?.id === property.id ? (
                      <select
                        value={editingProperty.status || 'pending'}
                        onChange={(e) => setEditingProperty(prev => ({
                          ...prev!,
                          status: e.target.value
                        }))}
                        className="w-full px-2 py-1 text-sm border rounded"
                      >
                        <option value="pending">Pending</option>
                        <option value="completed">Completed</option>
                        <option value="failed">Failed</option>
                        <option value="SOLD">Sold</option>
                      </select>
                    ) : (
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        property.status === 'completed'
                          ? 'bg-green-100 text-green-800'
                          : property.status === 'failed'
                          ? 'bg-red-100 text-red-800'
                          : property.status === 'SOLD'
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {property.status}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">
                      {new Date(property.created_at).toLocaleDateString()}
                      <br />
                      <span className="text-xs text-gray-400">
                        {new Date(property.created_at).toLocaleTimeString()}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end gap-3">
                      {editingProperty?.id === property.id ? (
                        <>
                          <button
                            onClick={saveEditing}
                            className="text-green-600 hover:text-green-900"
                          >
                            Save
                          </button>
                          <button
                            onClick={cancelEditing}
                            className="text-gray-600 hover:text-gray-900"
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={() => startEditing(property)}
                          className="text-indigo-600 hover:text-indigo-900"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={() => deleteProperty(property.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Magic Add Modal */}
        {showMagicModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl p-6 max-w-md w-full">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                Magic Add Property
              </h2>
              <form onSubmit={handleMagicAdd}>
                <div className="mb-4">
                  <label htmlFor="location" className="block text-sm font-medium text-gray-700 mb-1">
                    Enter Area
                  </label>
                  <input
                    type="text"
                    id="location"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    required
                    placeholder="Sherman, CT"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>
                <div className="flex justify-end gap-4">
                  <button
                    type="button"
                    onClick={() => setShowMagicModal(false)}
                    className="px-4 py-2 text-gray-700 hover:text-gray-900 transition-colors"
                  >
                    </button>
                  <button
                    type="submit"
                    disabled={magicLoading || !location.trim()}
                    className={`px-4 py-2 bg-purple-600 text-white rounded-lg flex items-center gap-2 ${
                      magicLoading || !location.trim()
                        ? 'opacity-50 cursor-not-allowed'
                        : 'hover:bg-purple-700'
                    } transition-colors`}
                  >
                    {magicLoading ? (
                      <>
                        <span className="animate-spin">⭘</span>
                        Processing...
                      </>
                    ) : (
                      <>
                        <Wand2 className="w-4 h-4" />
                        Add Property
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Image Lightbox */}
        {lightbox && (
          <ImageLightbox
            images={lightbox.images}
            currentIndex={lightbox.currentIndex}
            setLightbox={setLightbox}
            setError={setError}
            onClose={closeLightbox}
            onNext={nextImage}
            onPrevious={previousImage}
            property={lightbox.property}
            onGenerateImage={handleWebhook}
            error={error}
          />
        )}

        <div className="mt-6">
          <h2 className="text-lg font-medium text-gray-700 mb-4 text-center">
            Current Mode: <span className={`font-bold ${currentMode === 'live' ? 'text-red-600' : 'text-green-600'}`}>
              {currentMode.toUpperCase()}
            </span>
          </h2>
        </div>

        <div className="space-y-4 mb-6">
          <button
            onClick={() => handleModeChange('test')}
            className={`w-full py-3 px-6 rounded-lg font-medium transition-colors ${
              currentMode === 'test'
                ? 'bg-green-600 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Test Mode
          </button>
          
          <button
            onClick={() => handleModeChange('live')}
            className={`w-full py-3 px-6 rounded-lg font-medium transition-colors ${
              currentMode === 'live'
                ? 'bg-red-600 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Live Mode
          </button>
        </div>

        <button
          onClick={handleGoToLanding}
          className="w-full py-3 px-6 bg-black text-white rounded-lg font-medium hover:bg-gray-800 transition-colors flex items-center justify-center gap-2"
        >
          Go to Landing Page
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default Admin;